import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import TextArea from '../components/ui/TextArea';
import { selectAuthUserId } from '../redux/authSlice';
import {
  generateCourseFromPromptThunk,
  selectCourseError,
  selectCourseLoading,
} from '../redux/courseDetailsSlice';
import { selectProfile as selectStudentProfile } from '../redux/profileSlice';

const COURSE_CREATION_PROMPT_DRAFT_KEY = 'courseCreationPromptDraft';

const PROMPT_SUGGESTIONS = [
  'Create a practical intermediate course on building AI-powered apps with React, Supabase, and agent workflows.',
  'Design a beginner-friendly course that teaches data structures through visual simulations, coding exercises, and weekly projects.',
  'Build an advanced course on prompt systems, evaluation, and agent orchestration for real product teams.',
];

const CourseCreationChatPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector(selectAuthUserId);
  const profile = useSelector(selectStudentProfile);
  const loading = useSelector(selectCourseLoading);
  const error = useSelector(selectCourseError);
  const [prompt, setPrompt] = useState('');

  useEffect(() => {
    try {
      const savedDraft = window.localStorage.getItem(COURSE_CREATION_PROMPT_DRAFT_KEY);
      if (savedDraft) {
        setPrompt(savedDraft);
      }
    } catch {
      // Ignore storage failures.
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(COURSE_CREATION_PROMPT_DRAFT_KEY, prompt);
    } catch {
      // Ignore storage failures.
    }
  }, [prompt]);

  const handleGenerateCourse = async (event) => {
    event.preventDefault();

    const cleanedPrompt = String(prompt || '').trim();
    if (!cleanedPrompt || !userId) {
      return;
    }

    const action = await dispatch(
      generateCourseFromPromptThunk({
        prompt: cleanedPrompt,
        profileData: profile,
      })
    );

    if (generateCourseFromPromptThunk.fulfilled.match(action)) {
      window.localStorage.removeItem(COURSE_CREATION_PROMPT_DRAFT_KEY);
      navigate('/dashboard', {
        state: { openCourseId: action.payload?.id || null },
      });
    }
  };

  return (
    <div className="ml-course-creation-page">
      <div className="ml-course-creation-card">
        <div className="ml-course-creation-card__header">
          <h1 className="ml-course-creation-card__title">Create a Course with One Prompt</h1>
        </div>

        <div className="ml-course-creation-card__body">
          {error ? <div className="ml-form-error">{error}</div> : null}

          <form onSubmit={handleGenerateCourse} className="ml-course-creation-form">
            <h2 className="ml-form-section-title">Describe the course you want</h2>
            <p className="text-muted mb-3">
              Write one strong prompt with the subject, learner level, goals, scope, and teaching
              style you want. We&apos;ll turn it directly into a saved course.
            </p>

            <TextArea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={10}
              placeholder="Example: Create a project-based intermediate course on building prompt compilers. The learner already knows JavaScript and React. Cover prompt design, evaluation, graph-based workflows, backend integration, and hands-on projects. Keep it practical, implementation-heavy, and suitable for 6-8 weeks of study."
              required
            />

            <div className="d-flex flex-wrap gap-2 mt-3">
              {PROMPT_SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setPrompt(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>

            <div className="ml-form-actions">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  window.localStorage.removeItem(COURSE_CREATION_PROMPT_DRAFT_KEY);
                  setPrompt('');
                }}
              >
                Clear
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !String(prompt || '').trim()}
              >
                {loading ? 'Creating course...' : 'Create Course'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CourseCreationChatPage;
